/**
 * CypAdhdSupportingDocumentsPanel — the "Supporting Documents" card for CYP
 * ADHD clinician pages.
 *
 * Extracted from the inline implementation on the CYP DD Review page
 * (20_cypAdhdDD/0_DDReview.js) so both the DD Review and the PAD Review of
 * Assessment can share the same panel without drifting.
 *
 * Why a CYP-specific panel exists (rather than reusing the adult one)
 * -----------------------------------------------------------------------------
 * The adult DocumentPreviewPanel reads adult document slots
 * (documents.clinicalinterviewReport, qbtestReport, ...) and the adult
 * approval flags under dataModel['6_manageReports']. A CYP child never has
 * either: CYP renders into documents.cypAdhdCiReport / cypAdhdSsiReport and
 * keeps each stage's approval flag on that stage's reportContent document.
 * Looking up the adult keys silently returns nothing, which is why the PAD
 * had a missing panel until this component was introduced.
 *
 * What this panel shows
 * -----------------------------------------------------------------------------
 * One card per upstream CYP report that is BOTH rendered to PDF AND currently
 * approved (Clinical Interview, Semi-Structured Interview). That decision is
 * made by getCypMdtSupportingDocuments — the same helper both the DD Review
 * and the PAD Review use — so every page always agrees on which documents are
 * safe to reference. A section that was approved and then regenerated drops
 * back to "ready for review" and is hidden here, because its stored PDF may
 * no longer match the answers. When nothing qualifies, the whole panel is
 * hidden, matching the adult panel.
 *
 * Data sources
 * -----------------------------------------------------------------------------
 * The caller provides `documents` (the rendered-PDF metadata from the patient
 * header) and `reportContent` (the per-stage approval flags), rather than the
 * component reading a specific context itself. This keeps the component
 * reusable across pages that wire their data differently: the DD Review reads
 * documents from PatientDataContext (bridged), while the PAD Review reads
 * directly from ModularPatientDataContext.
 *
 * Preview
 * -----------------------------------------------------------------------------
 * "View" opens the shared DocumentEmbedDialog with the CYP report-stage
 * string ("CYP ADHD Clinical Interview"), which is what the embed Cloud
 * Function resolves to the stored PDF. Each card warms the embed-URL cache
 * on hover/focus so the first open is quick.
 */

import { useCallback, useMemo, useState } from 'react';
import { DocumentTextIcon, SearchIcon } from '@heroicons/react/solid';
import { getCypMdtSupportingDocuments } from '../utils/cypMdtSupportingDocuments';
import { useEmbeddedReportUrl } from '../hooks/useEmbeddedReportUrl';
import { formatDateTime, isValidTimestamp } from '../utils/timestampUtils';
import { DocumentEmbedDialog } from '../pages/providers/8_diagnosticDecision/components/DocumentEmbedDialog';

/**
 * One approved-report card. A separate component so the prefetch hook runs
 * once per card with a stable identity; calling the hook inside the parent's
 * .map() would break the rules of hooks when the list changes length.
 */
function SupportingDocumentCard({ patientId, clinicianId, report, onView }) {
  const { prefetch } = useEmbeddedReportUrl(
    patientId,
    report.reportStage,
    report.lastGenerated,
    clinicianId,
  );
  const warmCache = () => {
    try { prefetch?.(); } catch { /* best-effort */ }
  };

  return (
    <div className="flex flex-col items-center text-center">
      <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-2" aria-hidden="true" />
      <h4 className="font-medium text-gray-800 mb-1">{report.title}</h4>
      {isValidTimestamp(report.lastGenerated) && (
        <p className="text-xs text-gray-500 mt-1">
          Last updated on {formatDateTime(report.lastGenerated)}
        </p>
      )}
      <button
        type="button"
        onClick={() => onView(report)}
        onMouseEnter={warmCache}
        onFocus={warmCache}
        className="mt-3 px-4 py-2 bg-violet-900 hover:bg-violet-800 text-white text-sm font-semibold rounded-md transition-colors flex items-center gap-1"
        aria-label={`View ${report.title}`}
      >
        <SearchIcon className="h-4 w-4" aria-hidden="true" />
        View
      </button>
    </div>
  );
}

/**
 * @param {Object}  props
 * @param {string}  props.patientId    - Firestore patient ID.
 * @param {string}  props.clinicianId  - Signed-in clinician UID (for the embed URL).
 * @param {Object}  [props.documents]  - The patient header's `documents` map (PDF metadata).
 * @param {Object}  [props.reportContent] - Per-stage reportContent from ModularPatientDataContext.
 * @param {string}  [props.className]  - Additional CSS classes for the wrapper.
 */
export default function CypAdhdSupportingDocumentsPanel({
  patientId,
  clinicianId,
  documents,
  reportContent,
  className = '',
}) {
  const available = useMemo(
    () => getCypMdtSupportingDocuments(documents, reportContent),
    [documents, reportContent],
  );

  // The selected card drives the dialog: its CYP reportStage is what the
  // embed fetch needs, its title is what the clinician sees in the heading.
  const [selected, setSelected] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const handleView = useCallback((report) => {
    setSelected(report);
    setIsPreviewOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsPreviewOpen(false);
    // Clear the selection after the close animation so the heading and
    // iframe do not blank out mid-transition.
    setTimeout(() => setSelected(null), 300);
  }, []);

  // Hide entirely when nothing is previewable (parity with the adult panel).
  if (!patientId || available.length === 0) return null;

  return (
    <div className={`bg-gray-50 rounded-2xl border border-gray-200 p-6 ${className}`}>
      <h3 className="text-lg font-semibold mb-4 text-left">Supporting Documents</h3>
      <div className="flex flex-wrap gap-8">
        {available.map((report) => (
          <SupportingDocumentCard
            key={report.stageKey}
            patientId={patientId}
            clinicianId={clinicianId}
            report={report}
            onView={handleView}
          />
        ))}
      </div>

      <DocumentEmbedDialog
        isOpen={isPreviewOpen}
        onClose={handleClose}
        patientId={patientId}
        reportStage={selected?.reportStage}
        displayTitle={selected?.title}
        clinicianId={clinicianId}
        lastGenerated={selected?.lastGenerated || null}
      />
    </div>
  );
}
